import { useEffect, useState } from 'react';
import * as UpgradeProducts from 'self-service-api/constants/UpgradeProducts';
import AdminOnlyWrapper from 'ui-addon-upgrades/_core/common/components/AdminOnlyWrapper';
import { REQUEST_TRIAL } from 'ui-addon-upgrades/_core/common/constants/AdminModalTypes';
import { TEAM } from 'ui-addon-upgrades/_core/common/constants/AdminTypes';
import * as tracker from 'ui-addon-upgrades/_core/common/eventTracking/tracker';
import { getUpgradeDataTrackingProperties } from 'ui-addon-upgrades/ums/utils';
import { debounce, omit } from '../../../utils';
import TrialActivationButtonWithTooltip from './TrialActivationButtonWithTooltip';
import TrialActivationModalContainer from './TrialActivationModalContainer';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CLICK_DELAY = 50;
function sequence(...handlers) {
  return (...args) => {
    handlers.forEach(handler => handler && handler(...args));
  };
}

// This is a map of trial products that conflict with each other.
// If the user has a trial of one of these products, they cannot activate a trial of the other.
// For example, if the user has a trial of MARKETING_PLUS_ENTERPRISE, they cannot activate a trial of CMS_ENTERPRISE or MARKETING_ENTERPRISE.
const conflictingTrials = {
  [UpgradeProducts.MARKETING_PLUS_ENTERPRISE]: [UpgradeProducts.MARKETING_ENTERPRISE, UpgradeProducts.CMS_ENTERPRISE],
  [UpgradeProducts.MARKETING_PLUS_PROFESSIONAL]: [UpgradeProducts.MARKETING_PRO, UpgradeProducts.CMS_PROFESSIONAL],
  [UpgradeProducts.MARKETING_PRO]: [UpgradeProducts.MARKETING_PLUS_PROFESSIONAL],
  [UpgradeProducts.MARKETING_ENTERPRISE]: [UpgradeProducts.MARKETING_PLUS_ENTERPRISE],
  [UpgradeProducts.CMS_ENTERPRISE]: [UpgradeProducts.MARKETING_PLUS_ENTERPRISE],
  [UpgradeProducts.CMS_PROFESSIONAL]: [UpgradeProducts.MARKETING_PLUS_PROFESSIONAL]
};
const hasTrialedSameUpgradeProduct = (activeTrials, upgradeProduct) => {
  return activeTrials.includes(upgradeProduct);
};
const getConflictingTrial = (activeTrials, upgradeProduct) => {
  // find all potentialConflicts for the given upgradeProduct
  // return the first one, determined by the order of the `conflictingTrials` array
  return Object.keys(conflictingTrials).find(key => {
    var _conflictingTrials$ke;
    return ((_conflictingTrials$ke = conflictingTrials[key]) === null || _conflictingTrials$ke === void 0 ? void 0 : _conflictingTrials$ke.includes(upgradeProduct)) && activeTrials.includes(key);
  });
};
const TrialActivationButton = props => {
  const [showActivationModalContainer, setShowActivationModalContainer] = useState(false);
  const {
    activeTrials,
    expiredTrials,
    hideBackground,
    isMultiTrial,
    upgradeData,
    level,
    isUpgradePoint,
    onClick = () => {},
    onSuccess,
    shouldHideSurvey,
    successModalHeader,
    successModalSubText,
    successModalCtaText,
    showSuccessModalIllustration,
    children,
    isUpgradeModal = false,
    isLoadingTrackingProps,
    use = 'primary'
  } = props;
  const handleClick = () => {
    tracker.track('trialActivationButtonInteraction', Object.assign({
      action: 'clicked'
    }, getUpgradeDataTrackingProperties(upgradeData), {
      isMultiTrial,
      activeTrials,
      expiredTrials,
      trialId: upgradeData.trialId,
      duration: upgradeData.duration
    }));
    if (isUpgradePoint) {
      tracker.track('interaction', Object.assign({
        action: 'clicked upgrade point'
      }, getUpgradeDataTrackingProperties(upgradeData)));
      tracker.track('interaction', Object.assign({
        action: 'clicked trial activation',
        isPrimaryCta: level === 1,
        isSecondaryCta: level === 2
      }, getUpgradeDataTrackingProperties(upgradeData)));
    }
    setShowActivationModalContainer(true);
  };
  const debouncedHandleClick = debounce(handleClick, CLICK_DELAY);
  useEffect(() => {
    tracker.track('trialActivationButtonInteraction', Object.assign({
      action: 'viewed'
    }, getUpgradeDataTrackingProperties(upgradeData), {
      isMultiTrial,
      activeTrials,
      expiredTrials,
      trialId: upgradeData.trialId,
      duration: upgradeData.duration
    }));
    if (isUpgradePoint) {
      tracker.track('interaction', Object.assign({
        action: 'viewed trial activation',
        isPrimaryCta: level === 1,
        isSecondaryCta: level === 2
      }, getUpgradeDataTrackingProperties(upgradeData)));
    }
    return () => {
      debouncedHandleClick.reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const trialI18nKey = upgradeData.apiName ? `upgrades.trialActivationButton.${upgradeData.apiName}` : `upgrades.trialActivationButton.${upgradeData.upgradeProduct}`;
  const isTrialed = !!activeTrials && hasTrialedSameUpgradeProduct(activeTrials, upgradeData.upgradeProduct);

  // If `isTrialed` we have a 1:1 match with a product that the user has already trialed so we don't need to search for conflicts in other upgradeProducts
  const conflictingTrial = !isTrialed && activeTrials ? getConflictingTrial(activeTrials, upgradeData.upgradeProduct) : undefined;
  const onClickSequence = sequence(debouncedHandleClick, onClick);
  const propsToPass = omit(props, ['onClick', 'upgradeData', 'onSuccess', 'shouldHideSurvey', 'trialId', 'allowModal', 'successModalHeader', 'successModalSubText', 'successModalCtaText', 'showSuccessModalIllustration', 'isMultiTrial', 'activeTrials', 'expiredTrials', 'isUpgradePoint', 'hideBackground', 'canViewNewSeats', 'isUpgradeModal', 'isLoadingTrackingProps']);
  if (!upgradeData.trialId) return null;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [showActivationModalContainer && /*#__PURE__*/_jsx(TrialActivationModalContainer, {
      activeTrials: activeTrials,
      expiredTrials: expiredTrials,
      hideBackground: hideBackground,
      isMultiTrial: isMultiTrial,
      upgradeData: upgradeData,
      shouldHideSurvey: shouldHideSurvey,
      onSuccess: onSuccess,
      successModalHeader: successModalHeader,
      successModalSubText: successModalSubText,
      successModalCtaText: successModalCtaText,
      showSuccessModalIllustration: showSuccessModalIllustration,
      isUpgradeModal: isUpgradeModal
    }), /*#__PURE__*/_jsx(AdminOnlyWrapper, {
      upgradeData: upgradeData,
      adminType: TEAM,
      modalType: REQUEST_TRIAL,
      children: /*#__PURE__*/_jsx(TrialActivationButtonWithTooltip, {
        upgradeProduct: upgradeData.upgradeProduct,
        onClick: onClickSequence,
        isTrialed: isTrialed,
        isLoading: !!isLoadingTrackingProps,
        trialI18nKey: trialI18nKey,
        showActivationModalContainer: showActivationModalContainer,
        propsToPass: propsToPass,
        use: use,
        conflictingTrial: conflictingTrial,
        children: children
      })
    })]
  });
};
export default TrialActivationButton;